// ** UI
import Box from '@mui/material/Box';

// ** Components
import ContentContainer from './ContentContainer';

// ** Hooks
import { useSettings } from '@/hooks/useSettings';

// ** Types
import type { SxProps, Theme } from '@mui/material/styles';

type SectionProps = {
	children: React.ReactNode;
	backgroundColor?: string;
	maxWidthPx?: number;
	containerSx?: SxProps<Theme>;
	fullWidth?: boolean;
};

const Section = ({ children, backgroundColor, maxWidthPx, containerSx, fullWidth = false }: SectionProps) => {
	const { settings } = useSettings();

	// 1. Determine the Base Width from Global Settings
	const isBoxed = settings?.contentWidth === 'boxed';
	const baseWidth = isBoxed ? 1440 : 1920;

	// 2. Local override (from Strapi) takes priority, otherwise use Global
	const finalMaxWidth = maxWidthPx ? maxWidthPx : baseWidth;

	return (
		<Box
			component="section"
			sx={{
				width: '100%',
				...(backgroundColor ? { bgcolor: backgroundColor } : null),
				// Full-bleed blocks (e.g. carousels) use 100vw in inner calculations;
				// clip + cap width avoids a horizontal page scrollbar on Windows.
				...(fullWidth ? { maxWidth: '100%', overflowX: 'clip' } : null)
			}}
		>
			<ContentContainer
				fullWidth={fullWidth}
				maxWidthPx={finalMaxWidth}
				disableGutters
				sx={[
					(theme: Theme) => ({ px: fullWidth ? 0 : theme.layout.pagePx }),
					...(Array.isArray(containerSx) ? containerSx : containerSx ? [containerSx] : [])
				]}
			>
				{children}
			</ContentContainer>
		</Box>
	);
};

export default Section;
